import React, { useState, useRef, useCallback, useMemo, useEffect, memo } from 'react';
import styles from './styles.module.css';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import cn from 'classnames';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { IoWarningOutline } from 'react-icons/io5';

/**
 * CustomSelect - переиспользуемый компонент выпадающего списка с поиском
 *
 * @param {Array} options - массив опций [{name: string, value: any}]
 * @param {any} value - текущее выбранное значение
 * @param {string} title - заголовок/label над селектом
 * @param {number|string} width - ширина компонента (число в px или строка '100%')
 * @param {function} setFieldValue - функция для установки значения (Formik-совместимая)
 * @param {string} valuesName - имя поля для setFieldValue
 * @param {boolean} disabled - заблокирован ли селект (default: false)
 * @param {string} error - текст ошибки валидации
 * @param {string} className - дополнительный CSS класс для кастомизации извне
 * @param {boolean} withoutAll - убрать авто-опцию "все" (default: false)
 * @param {string} allLabel - текст авто-опции "все" (default: 'Все'). Используй 'Выбрать' когда список — это выбор конкретного значения, а не фильтр
 * @param {string} placeholder - текст плейсхолдера (default: 'Выбрать')
 * @param {boolean} searchable - включить поле поиска (default: true)
 * @param {function} renderOption - кастомная функция рендеринга опции (option) => ReactNode
 * @param {function} renderSelected - кастомная функция рендеринга выбранного значения (option) => ReactNode
 */
const CustomSelect = memo(({
    options = [],
    value,
    title,
    width = 200,
    setFieldValue,
    valuesName,
    disabled = false,
    error,
    className,
    withoutAll = false,
    allLabel = 'Все',
    placeholder = 'Выбрать',
    searchable = true,
    renderOption,
    renderSelected,
    dropUp = false,
    highlight = false,
    hint,
}) => {
    // === STATE ===
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // === REFS ===
    const selectRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // === MEMOIZED DATA ===
    const data = useMemo(() => {
        if (!options || options.length === 0) return [];
        return withoutAll ? options : [{ name: allLabel, value: '' }, ...options];
    }, [options, withoutAll, allLabel]);

    const filteredOptions = useMemo(() => {
        if (!searchQuery.trim()) return data;
        const query = searchQuery.toLowerCase().trim();
        return data.filter(option => option.name?.toLowerCase().includes(query));
    }, [data, searchQuery]);

    const selectedDisplayName = useMemo(() => {
        if (value === undefined || value === null || value === '') {
            return withoutAll ? '' : allLabel;
        }
        const found = data?.find(item => item.value == value);
        return found?.name || '';
    }, [value, data, withoutAll, allLabel]);

    // === HANDLERS ===
    const handleOpen = useCallback(() => {
        if (!disabled) {
            setIsOpen(true);
            setHighlightedIndex(-1);
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [disabled]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
    }, []);

    const handleToggle = useCallback(() => {
        if (isOpen) {
            handleClose();
        } else {
            handleOpen();
        }
    }, [isOpen, handleOpen, handleClose]);

    const handleSelect = useCallback((option) => {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        if (setFieldValue && valuesName) {
            setFieldValue(valuesName, option.value);
        }
    }, [setFieldValue, valuesName]);

    const handleSearchChange = useCallback((e) => {
        const newQuery = e.target.value;
        setSearchQuery(newQuery);
        setHighlightedIndex(-1);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
                e.preventDefault();
                handleOpen();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                handleClose();
                break;
            case 'Tab':
                handleClose();
                break;
            default:
                break;
        }
    }, [isOpen, highlightedIndex, filteredOptions, handleOpen, handleClose, handleSelect]);

    // === EFFECTS ===
    useOnClickOutside(selectRef, handleClose);

    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('li');
            items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [highlightedIndex]);

    useEffect(() => {
        setHighlightedIndex(-1);
    }, [filteredOptions.length]);

    // === STYLES ===
    const computedWidth = typeof width === 'number' ? width + 20 : width;

    const containerClass = cn(styles.container, className);
    const selectBoxClass = cn(styles.selectBox, {
        [styles.selectBoxOpen]: isOpen,
        [styles.selectBoxDisabled]: disabled,
        [styles.selectBoxError]: error,
    });

    const getOptionClass = (index, option) => cn(styles.option, {
        [styles.optionHighlighted]: index === highlightedIndex,
        [styles.optionSelected]: value !== null && value !== undefined && option.value == value,
    });

    // === RENDER ===
    return (
        <div className={containerClass} ref={selectRef} style={{ width: computedWidth }}>
            {title && <label className={cn(styles.label, { [styles.greenLabel]: highlight })}>{title}</label>}

            <div
                className={selectBoxClass}
                onClick={!disabled ? handleToggle : undefined}
                onKeyDown={handleKeyDown}
                tabIndex={disabled ? -1 : 0}
                role="combobox"
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                aria-disabled={disabled}
            >
                <div className={styles.selectedText}>
                    {renderSelected
                        ? (value !== undefined && value !== null && value !== ''
                            ? renderSelected(options?.find(item => item.value == value))
                            : placeholder)
                        : (selectedDisplayName || placeholder)
                    }
                </div>
                <div className={styles.arrow}>
                    {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                </div>
            </div>

            {isOpen && !disabled && (
                <div className={cn(styles.dropdown, { [styles.dropdownUp]: dropUp })}>
                    {searchable && options.length > 5 && (
                        <div className={styles.searchWrapper}>
                            <input
                                ref={inputRef}
                                type="text"
                                className={styles.searchInput}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown}
                                placeholder="Поиск..."
                                autoComplete="off"
                            />
                        </div>
                    )}

                    <ul ref={listRef} className={styles.optionsList} role="listbox">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <li
                                    key={`${option.value}-${valuesName}-${index}`}
                                    className={getOptionClass(index, option)}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => setHighlightedIndex(index)}
                                    role="option"
                                    aria-selected={value !== null && value !== undefined && option.value == value}
                                >
                                    {renderOption ? renderOption(option) : option.name}
                                </li>
                            ))
                        ) : (
                            <li className={styles.noResults}>Ничего не найдено</li>
                        )}
                    </ul>
                </div>
            )}

            {!!error && <span className={styles.errorText}>{error}</span>}
            {!error && hint && <span className={styles.hintText}><IoWarningOutline />{hint}</span>}
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.value === nextProps.value &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.error === nextProps.error &&
        prevProps.options === nextProps.options &&
        prevProps.title === nextProps.title &&
        prevProps.width === nextProps.width &&
        prevProps.className === nextProps.className &&
        prevProps.withoutAll === nextProps.withoutAll &&
        prevProps.allLabel === nextProps.allLabel &&
        prevProps.valuesName === nextProps.valuesName &&
        prevProps.searchable === nextProps.searchable &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.renderOption === nextProps.renderOption &&
        prevProps.renderSelected === nextProps.renderSelected &&
        prevProps.dropUp === nextProps.dropUp &&
        prevProps.highlight === nextProps.highlight &&
        prevProps.hint === nextProps.hint
    );
});

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
